import {
	compressImageForUpload,
	ImageCompressionError,
	isWithinTarget,
	type ImageCompressionTarget,
} from "./imageCompression";
import {measureImage, offscreenImageCodec} from "./offscreenImageCodec";
import {canvasWebpEncoder} from "./webpEncoders";

/**
 * The one entry point every image picker calls (ui#601).
 *
 * Hands the work to a worker thread, and only does it here if there is no worker to be had. Nothing
 * above this line knows about WebAssembly, canvases or threads: a picker passes the file the sender
 * chose and its own {@link ImageCompressionTarget}, and gets back a file the api will accept.
 */

/** What the main thread asks the worker to do. */
export interface CompressionRequest {
	file: File;
	target: ImageCompressionTarget;
}

/** What comes back. A failure carries text only — an Error does not survive structured cloning. */
export type CompressionResponse =
	| {ok: true; file: File}
	| {ok: false; message: string};

/**
 * Compresses `file` until it fits `target`, or returns it untouched when it already does.
 *
 * @throws ImageCompressionError when the picture cannot be decoded or compressed. Callers MUST turn
 *   this into visible, translated copy: it is the only way a pick can fail now that no size limit
 *   rejects one, so swallowing it would leave a picker that quietly does nothing.
 */
export async function compressPickedImage(
	file: File,
	target: ImageCompressionTarget,
): Promise<File> {
	// Asked HERE, before any thread is committed to. Most picks need no work at all, and starting a
	// worker for one costs a chunk fetch, a WebAssembly instantiation and two structured clones of the
	// file — to hand back bytes identical to the ones passed in. Checking first also keeps the promise
	// this function's contract makes: an untouched file comes back as the very same object, which it
	// cannot be once it has crossed a thread boundary.
	if (await isWithinTarget(file, target, measureImage)) {
		return file;
	}
	if (typeof Worker === "undefined") {
		return compressOnThisThread(file, target);
	}
	let worker: Worker;
	try {
		// `new URL(…, import.meta.url)` is what makes Vite emit the worker as its own chunk, taking the
		// codec — and the lazily-imported wasm behind it — out of the app's initial bundle.
		worker = new Worker(new URL("./imageCompressionWorker.ts", import.meta.url), {type: "module"});
	} catch {
		// Workers can be blocked outright (a strict sandbox, a stale browser). That is not the sender's
		// problem, so compress here instead and accept the jank.
		return compressOnThisThread(file, target);
	}
	try {
		return await runInWorker(worker, {file, target});
	} finally {
		worker.terminate();
	}
}

/**
 * How long to wait for a worker that has gone quiet.
 *
 * This is a WATCHDOG, not a performance budget. A browser that kills a worker for memory — the failure
 * the owner called out for very large images on mobile Safari — does so SILENTLY: no `error` event, no
 * message, nothing to react to. Without a bound, that leaves the picker spinning forever on the one
 * input most likely to cause it.
 *
 * Deliberately far above any legitimate run so it can never fire spuriously: a 12 MB, 4.3-megapixel
 * source measured 3.6 s on a desktop, and this allows an order of magnitude more for a slow phone.
 */
const WORKER_WATCHDOG_MS = 90_000;

function runInWorker(worker: Worker, request: CompressionRequest): Promise<File> {
	return new Promise((resolve, reject) => {
		const watchdog = setTimeout(() => {
			// No main-thread retry: a worker killed for memory means this device could not hold the
			// picture, and trying again here would take the whole tab down with it.
			reject(new ImageCompressionError(
				"The image could not be processed — it may be too large for this device"));
		}, WORKER_WATCHDOG_MS);
		const settle = (act: () => void) => {
			clearTimeout(watchdog);
			act();
		};

		worker.addEventListener("message", (event: MessageEvent<CompressionResponse>) => {
			const response = event.data;
			settle(() => {
				if (response.ok) {
					resolve(response.file);
				} else {
					// The worker already tried its own encoder fallback, so this is a real failure of the
					// picture rather than of the thread. Retrying here would only fail again, slower.
					reject(new ImageCompressionError(response.message));
				}
			});
		});
		// Fires when the worker SCRIPT fails to load or throws at module scope — a different failure from
		// a picture that cannot be compressed, and the one case where this thread should try instead.
		worker.addEventListener("error", event => {
			event.preventDefault();
			settle(() => compressOnThisThread(request.file, request.target).then(resolve, reject));
		});
		// A response that cannot be structured-cloned would otherwise be silence.
		worker.addEventListener("messageerror", () => {
			settle(() => reject(new ImageCompressionError("The compressed image could not be read back")));
		});
		worker.postMessage(request);
	});
}

/**
 * The no-worker path. Uses the canvas encoder rather than the WebAssembly one: if the worker could not
 * be had, running a wasm encoder on the main thread would freeze the page for the whole search, and the
 * canvas produces the same format anyway.
 */
function compressOnThisThread(file: File, target: ImageCompressionTarget): Promise<File> {
	return compressImageForUpload(file, target, offscreenImageCodec(canvasWebpEncoder));
}
